"use client";
import React, { useState, useEffect } from "react";
import { Routes } from "@/config/routes";
import { getLocalStorageItem } from "@/helpers/localStorage";
import { CircularProgress, Grid, Skeleton, Typography } from "@mui/material";
import classNames from "classnames";
import Image from "next/image";
import Link from "next/link";
import { usePathname, useRouter } from "next/navigation";

import styles from "../styles/component/sidebar.module.scss";

import logo from "../public/images/logo.webp";
import { getSystemRoleLabel } from "./roles";

import { useGetParentDetails } from "@/helpers/hooks/parentdetails/getparentdetails";


import {
  ArrowRightLeft,
  Calculator,
  CalendarDays,
  ChartColumn,
  CircleUserRound,
  Gift,
  LayoutDashboard,
  MessageSquare,
  MessageSquareText,
  Newspaper,
  ReceiptText,
  SquareMousePointer,
  SquareUser,
  TicketPercent,
  UserLock,
  UserMinus,
  Users,
  Users2,
  Utensils,
} from "lucide-react";
import {
  GroupAdd,
  GroupAddOutlined,
  SyncAltOutlined,
  TransferWithinAStation,
} from "@mui/icons-material";

export default function Sidebar({ toggleSidebar, isMediumScreen }) {
  const router = useRouter();
  const path = usePathname();
  const [mounted, setMounted] = useState(false);
  const [role, setRole] = useState(null);
  const pathname = "/" + path.split("/")[1];
  const { data, isFetching } = useGetParentDetails();
  const parentDetails = data?.data;
  useEffect(() => {
    setRole(getLocalStorageItem("role"));
  }, [role]);
  useEffect(() => {
    setMounted(true); // render only after hydration
  }, []);
  const menuItems = {
    super_admin: [
      {
        href: Routes.mamAdmin,
        icon: <UserLock />,
        iconActive: <UserLock />,
        label: "Admin Management",
      },
      {
        href: Routes.dietPlan,
        icon: <Utensils />,
        iconActive: <Utensils />,
        label: "Diet Plans",
      },
      {
        href: Routes.mealplantemplate,
        icon: <LayoutDashboard />,
        iconActive: <LayoutDashboard />,
        label: "Manage Templates & Foods",
      },
      {
        href: Routes.foodconfiguration,
        icon: <SquareMousePointer />,
        iconActive: <SquareMousePointer />,
        label: "Food/Recipe Diet Plan Usage",
      },
      {
        href: Routes.manageBlog,
        icon: <Newspaper />,
        iconActive: <Newspaper />,
        label: "Blog",
      },
      {
        href: Routes.request,
        icon: <Users />,
        iconActive: <Users />,
        label: "Trainer Requests",
      },
      {
        href: Routes.alltransactionlist,
        icon: <ReceiptText />,
        iconActive: <ReceiptText />,
        label: "All Transaction",
      },
      {
        href: Routes.manageCoupons,
        icon: <TicketPercent />,
        iconActive: <TicketPercent />,
        label: "Manage Coupons",
      },
      {
        href: Routes.transferAdmin,
        icon: <ArrowRightLeft />,
        iconActive: <ArrowRightLeft />,
        label: "Transfer Admin",
      },
      {
        href: Routes.customerRemoveRequest,
        icon: <UserMinus />,
        iconActive: <UserMinus />,
        label: "Customer Delete Requests",
      },
      {
        href: Routes.referral,
        icon: <GroupAddOutlined />,
        iconActive: <GroupAddOutlined />,
        label: "Referrals",
      },
      {
        href: Routes.reports,
        icon: <ChartColumn />,
        iconActive: <ChartColumn />,
        label: "Reports",
      },
      {
        href: Routes.userManagement,
        icon: <Users2 />,
        iconActive: <Users2 />,
        label: "User Management",
      },
    ],
    admin: [
      {
        href: Routes.dashboard,
        icon: <LayoutDashboard />,
        iconActive: <LayoutDashboard />,
        label: "Dashboard",
      },
      {
        href: Routes.trainer,
        icon: <SquareUser />,
        iconActive: <SquareUser />,
        label: "Trainer & Customer",
      },
      {
        href: Routes.dietPlan,
        icon: <Utensils />,
        iconActive: <Utensils />,
        label: "Diet Plans",
      },
      {
        href: Routes.mealplantemplate,
        icon: <LayoutDashboard />,
        iconActive: <LayoutDashboard />,
        label: "Manage Templates & Foods",
      },
      {
        href: Routes.foodconfiguration,
        icon: <SquareMousePointer />,
        iconActive: <SquareMousePointer />,
        label: "Food/Recipe Diet Plan Usage",
      },
      {
        href: Routes.manageBlog,
        icon: <Newspaper />,
        iconActive: <Newspaper />,
        label: "Blog",
      },
      {
        href: Routes.message,
        icon: <MessageSquareText />,
        iconActive: <MessageSquareText />,
        label: "Message",
      },
      {
        href: Routes.customerRemoveRequest,
        icon: <UserMinus />,
        iconActive: <UserMinus />,
        label: "Customer Delete Requests",
      },
    ],
    trainer: [
      {
        href: Routes.dashboard,
        icon: <LayoutDashboard />,
        iconActive: <LayoutDashboard />,
        label: "Dashboard",
      },
      {
        href: Routes.customer,
        icon: <Users2 />,
        iconActive: <Users2 />,
        label: "Customers",
      },
      {
        href: Routes.mealplantemplate,
        icon: <LayoutDashboard />,
        iconActive: <LayoutDashboard />,
        label: "Manage Templates & Foods",
      },
      {
        href: Routes.calculators,
        icon: <Calculator />,
        iconActive: <Calculator />,
        label: "BMR / TDEE / MACRO Calculator",
      },

      {
        href: Routes.message,
        icon: <MessageSquareText />,
        iconActive: <MessageSquareText />,
        label: "Message",
      },
      // {
      //   href: Routes.customerplanrequesttotrainer,
      //   icon: reportsIcon,
      //   iconActive: reportsIconActive,
      //   label: "Diet Plan Requests",
      // },
      {
        href: Routes.trainersubscriptionplan,
        icon: <CalendarDays />,
        iconActive: <CalendarDays />,
        label: "Subscription",
      },

      {
        href: Routes.rewards,
        icon: <Gift />,
        iconActive: <Gift />,
        label: "Refer & Earn",
      },
    ],
    customer: [
      {
        href: Routes.dashboard,
        icon: <LayoutDashboard />,
        iconActive: <LayoutDashboard />,
        label: "Dashboard",
      },
      {
        href: Routes.dietPlan,
        icon: <Utensils />,
        iconActive: <Utensils />,
        label: "Diet Plans",
      },
      {
        href: Routes.calculators,
        icon: <Calculator />,
        iconActive: <Calculator />,
        label: "BMR / TDEE / MACRO Calculator",
      },
      {
        href: Routes.message,
        icon: <MessageSquareText />,
        iconActive: <MessageSquareText />,
        label: "Message",
      },
      {
        href: Routes.customersubscriptionplan,
        icon: <CalendarDays />,
        iconActive: <CalendarDays />,
        label: "Subscription",
      },
      {
        href: Routes.profile,
        icon: <CircleUserRound />,
        iconActive: <CircleUserRound />,
        label: "Profile",
      },
      {
        href: Routes.rewards,
        icon: <Gift />,
        iconActive: <Gift />,
        label: "Refer & Earn",
      },
    ],
  };

  const renderMenuItem = (item) => {
    const isActive = pathname === item.href;

    if (!mounted) return null;

    // Decide which icon to use
    const IconComponent = pathname === item.href ? item.iconActive : item.icon;

    return (
      <Link
        key={item.href}
        href={item.href}
        onClick={() => isMediumScreen && toggleSidebar()}
        className={classNames(
          styles.menuItem,
          isActive && styles.menuItemActive,
          "flex items-center space-x-2 group",
        )}
        style={{ padding: "8px 8px" }}
      >
        {typeof IconComponent === "string" || IconComponent?.src ? (
          <Image
            src={IconComponent}
            height={20}
            width={20}
            alt={`${item.label}-icon`}
            style={{
              filter: isActive
                ? "none"
                : "brightness(0) saturate(100%) invert(50%) sepia(8%) saturate(3%) hue-rotate(174deg) brightness(97%) contrast(91%)",
            }}
          />
        ) : (
          <span
            className="flex flex-col h-full justify-center"
            style={{ width: 20, height: 20, justifyContent: "center" }}
          >
            {React.cloneElement(IconComponent, {
              size: 20,
              className: classNames(
                isActive
                  ? "text-white group-hover:text-black"
                  : "text-[#8b8b8b] group-hover:text-gray-700",
              ),
            })}
          </span>
        )}

        <Typography
          sx={{
            fontWeight: 300,
            color: `${isActive ? "white" : "black"} !important`,
            "&:hover": {
              color: `${isActive ? "white" : "black"} !important`,
            },
            "&:hover, a:hover &": {
              color: "black !important",
            },
          }}
        >
          {item.label}
        </Typography>
      </Link>
    );
  };
  const handleMessChat = () => {
    router.push(`${Routes.message}?user_id=${parentDetails?.id}`);
  };
  return (
    <Grid item xl={2} lg={3}>
      <div className={styles.sidebarSection}>
        <Grid
          item
          xl={2}
          lg={3}
          sx={{
            display: {
              xl: "flex",
              lg: "flex",
              md: "none",
              sm: "none",
              xs: "none",
            },
            justifyContent: "space-between",
          }}
          borderBottom={1}
          borderColor={"#dddddd"}
        >
          {/* <Link > */}
          <div className="flex items-center">
            <div className="w-16 h-16 rounded-full flex items-center justify-center mr-2">
              <Image
                src={logo}
                alt="Logo"
                width={64}
                height={64}
                className="rounded-full object-fill"
              />
            </div>
            <span className="text-xl font-semibold text-green-700">
              Macros & Meals
            </span>
          </div>
          {/* <Image
            src={sidebarLogo}
            // height={80}
            // width={229}
            alt="brand-logo"
            className="h-[75px] w-full px-6 object-fit"
            // className={style.sidebarLogo}
            // style={{ height: "auto", width: "auto" }}
            priority
          /> */}
        </Grid>

        <div className="flex flex-1 flex-col items-center h-full min-w-[265px]">
          {/* Sidebar Menu */}
          <div className="w-full mb-3 p-2">
            {role && menuItems[role]?.map(renderMenuItem)}
          </div>

          {/* <h1 className="w-full border-t border-gray-200"></h1> */}
          {/* Divider with text */}

          {/* {role === "super_admin" || "admin" && (
            
          )} */}
          {role !== "admin" && role !== "super_admin" && (
            <>
              <div className="w-full flex items-center justify-center relative my-2">
                {" "}
                <span className="absolute inset-x-0 top-1/2 border-t border-gray-200"></span>{" "}
                <Typography
                  variant="caption"
                  sx={{
                    color: "#A0AEC0",
                    fontStyle: "italic",
                    backgroundColor: "white",
                    px: 1,
                    zIndex: 1,
                  }}
                >
                  {" "}
                  Your point of contact{" "}
                </Typography>{" "}
              </div>{" "}
              {/* Profile Section */}{" "}
              {mounted &&
                (isFetching ? (
                  <div className="flex flex-col items-center text-center bg-gradient-to-t from-gray-50 to-white p-4 rounded-xl shadow-sm">
                    {/* Avatar Skeleton */}
                    <Skeleton
                      variant="circular"
                      width={70}
                      height={70}
                      className="mb-3"
                    />

                    {/* Name Skeleton */}
                    <Skeleton
                      variant="rectangular"
                      width={120}
                      height={24}
                      className="mb-2 rounded-md"
                    />

                    {/* Role Skeleton */}
                    <Skeleton
                      variant="rectangular"
                      width={80}
                      height={18}
                      className="rounded-md"
                    />
                  </div>
                ) : (
                  <div className="flex flex-col items-center text-center bg-gradient-to-t from-gray-50 to-white p-4 rounded-xl shadow-sm">
                    <div className="relative w-[70px] h-[70px] rounded-full overflow-hidden mb-3 border-2 border-green-400 shadow-md hover:scale-105 transition-transform duration-300">
                      <Image
                        src={parentDetails?.profile_image || logo}
                        alt="User Avatar"
                        fill
                        style={{ objectFit: "cover" }}
                      />
                    </div>

                    <div
                      className="relative bg-gradient-to-r from-green-500 to-green-600 text-white px-6 py-2 rounded-md shadow-md cursor-pointer"
                      onClick={handleMessChat}
                    >
                      <Typography
                        sx={{
                          fontWeight: 600,
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                        }}
                        variant="body1"
                        title={parentDetails?.full_name || ""}
                      >
                        <MessageSquareText size={18} strokeWidth={2} />
                        {(parentDetails?.full_name?.length > 20
                          ? parentDetails.full_name.substring(0, 20) + "..."
                          : parentDetails?.full_name) || ""}
                      </Typography>

                      <Typography
                        variant="body2"
                        sx={{ fontSize: "0.8rem", opacity: 0.9 }}
                      >
                        {getSystemRoleLabel(parentDetails?.role) || "Member"}
                      </Typography>
                    </div>
                  </div>
                ))}
            </>
          )}
        </div>
      </div>
    </Grid>
  );
}
